import L from 'leaflet';
import { ACTIVITY_CATEGORIES } from '../../constants/travelerProfiles';
import { getSvgIconHtml } from '../../utils/iconSvgStrings';

// Helper to check if itinerary was created within the last 5 minutes
export const isRecentlyCreated = (createdAt) => {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const now = new Date();
  return (now - created) < 5 * 60 * 1000;
};

// Helper to format date
export const formatDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Helper to get date for a specific day number
export const getDateForDay = (startDate, dayNumber) => {
  if (!startDate) return null;
  // Parse YYYY-MM-DD as local midnight to avoid UTC offset shifting the date
  const [year, month, day] = startDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + (dayNumber - 1));
  return formatDate(date);
};

// Day colors for map markers and route lines
export const DAY_COLORS = [
  '#E07A50', '#5A8FA8', '#C9983A', '#627E8E', '#7FB5C8',
  '#C06840', '#6AAFC0', '#B89030', '#4E7E90', '#8FA8A0',
];

// Create colored marker icon
export const createDayIcon = (dayNumber) => {
  const color = DAY_COLORS[(dayNumber - 1) % DAY_COLORS.length];
  return L.divIcon({
    className: 'custom-map-marker',
    html: `<div style="
      background-color: ${color};
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 12px;
    ">${dayNumber}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
};

/**
 * Compute a location label for each day based on activity locations.
 * - If activities span multiple cities → show city per day
 * - If all in the same city → show area/neighborhood per day (when available)
 */
export const getDayLocationLabels = (activities, days) => {
  const labels = {};

  days.forEach(day => {
    const dayActivities = activities.filter(a => a.day_number === day);
    // Use city_name field from AI generation — deduplicate and join
    const cities = [...new Set(
      dayActivities
        .map(a => a.city_name)
        .filter(Boolean)
    )];
    labels[day] = cities.length > 0 ? cities.join(', ') : null;
  });

  return labels;
};

// Get category info helper
export const getCategoryInfo = (category) => {
  return ACTIVITY_CATEGORIES.find(c => c.value === category) || { iconName: 'MapPin', color: '#71717A' };
};

// Activity marker with inline SVG icon inside day-colored circle
export const createCategoryIcon = (activity) => {
  const dayColor = DAY_COLORS[(activity.day_number - 1) % DAY_COLORS.length];
  return L.divIcon({
    className: 'custom-map-marker',
    html: `<div style="
      background: ${dayColor};
      width: 34px; height: 34px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex; align-items: center; justify-content: center;
    ">${getSvgIconHtml(activity.category, 'white', 16)}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -17],
  });
};

// Distinct hotel marker (square-ish columbia blue)
export const createHotelIcon = () => L.divIcon({
  className: 'custom-map-marker',
  html: `<div style="
    background: #4A7B91;
    width: 34px; height: 34px;
    border-radius: 8px;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex; align-items: center; justify-content: center;
  ">${getSvgIconHtml('accommodation', 'white', 16)}</div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
  popupAnchor: [0, -17],
});

// Haversine distance in km between two lat/lng points
export const haversineKm = (lat1, lng1, lat2, lng2) => {
  const toRad = (deg) => deg * Math.PI / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Compute total road distance for a day's activities (haversine × 1.3 road factor)
export const computeDayDriveStats = (dayActivities) => {
  const withCoords = dayActivities
    .filter(a => a.latitude && a.longitude && (Math.abs(a.latitude) > 0.1 || Math.abs(a.longitude) > 0.1))
    .sort((a, b) => (a.position || 0) - (b.position || 0));
  if (withCoords.length < 2) return null;
  let totalKm = 0;
  for (let i = 0; i < withCoords.length - 1; i++) {
    totalKm += haversineKm(
      withCoords[i].latitude, withCoords[i].longitude,
      withCoords[i + 1].latitude, withCoords[i + 1].longitude
    );
  }
  totalKm *= 1.3; // road factor for winding roads
  const driveMinutes = Math.round(totalKm / 55 * 60); // ~55 km/h avg for van/road trips
  return { km: Math.round(totalKm), minutes: driveMinutes };
};

// Format drive stats for display
export const formatDriveStats = (stats) => {
  if (!stats) return null;
  const time = stats.minutes < 60
    ? `${stats.minutes}min`
    : `${Math.floor(stats.minutes / 60)}h${stats.minutes % 60 > 0 ? `${stats.minutes % 60}m` : ''}`;
  return `${stats.km} km \u00b7 ${time} drive`;
};
